// API Configuration
// Backend URL is determined by environment

const getApiBaseUrl = (): string => {
  // For production builds, use the environment variable set at build time
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // For development, use localhost
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:8000';
  }

  // Fallback: use relative path for Nginx proxy
  // This allows the frontend to proxy requests to backend via /api path
  // solving Mixed Content issues (HTTPS frontend -> HTTP backend)
  return '';
};

export const API_BASE_URL = getApiBaseUrl();

const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_REFRESH_TOKEN_KEY = 'auth_refresh_token';
const AUTH_USERNAME_KEY = 'auth_username';
const AUTH_EXPIRED_EVENT = 'tkr:auth:expired';
const AUTH_EXPIRED_FLAG_KEY = 'tkr_auth_expired';
const DEBUG_HTTP_FLAG_KEY = 'tkr_debug_http';

const isHttpDebugEnabled = (): boolean => {
  try {
    return (localStorage.getItem(DEBUG_HTTP_FLAG_KEY) || '') === '1';
  } catch {
    return false;
  }
};

const makeRequestId = (): string => {
  try {
    if (
      typeof crypto !== 'undefined' &&
      'randomUUID' in crypto &&
      typeof crypto.randomUUID === 'function'
    ) {
      return String(crypto.randomUUID());
    }
  } catch {
    // ignore
  }
  return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const notifyAuthExpired = () => {
  // Clear token first so the app will render Login on next tick.
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USERNAME_KEY);
  // Flag so Login screen can show a helpful message.
  try {
    sessionStorage.setItem(AUTH_EXPIRED_FLAG_KEY, '1');
  } catch {
    // ignore
  }
  // Notify same-tab listeners (storage event won't fire in same tab).
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
};

let refreshInFlight: Promise<boolean> | null = null;

const tryRefreshAccessToken = async (): Promise<boolean> => {
  const refreshToken = localStorage.getItem(AUTH_REFRESH_TOKEN_KEY) || '';
  if (!refreshToken) return false;

  const username = localStorage.getItem(AUTH_USERNAME_KEY) || '';
  try {
    const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken, username }),
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { access_token?: string };
    if (!data?.access_token) return false;
    localStorage.setItem(AUTH_TOKEN_KEY, data.access_token);
    return true;
  } catch {
    return false;
  }
};

// Helper function for making authenticated API calls
export const fetchWithAuth = async (
  url: string,
  options: RequestInit = {},
  _retry: boolean = false
): Promise<Response> => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const debug = isHttpDebugEnabled();
  const requestId = makeRequestId();
  const startedAt = performance.now();

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // Only set JSON Content-Type when we actually send a JSON body.
  // - Setting Content-Type on GET can trigger an unnecessary CORS preflight.
  // - For FormData/Blob/URLSearchParams bodies the browser must set
  //   Content-Type itself (so it can pick the right multipart boundary or
  //   form-urlencoded type); overriding it here would break the upload.
  const body = options.body as unknown;
  const isStructuredBody =
    (typeof FormData !== 'undefined' && body instanceof FormData) ||
    (typeof Blob !== 'undefined' && body instanceof Blob) ||
    (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) ||
    (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream);
  if (body != null && !isStructuredBody && !headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Propagate request id so backend logs can be correlated with browser logs.
  if (!headers['X-Request-ID'] && !headers['x-request-id']) {
    headers['X-Request-ID'] = requestId;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers,
    });
  } catch (e) {
    if (debug) {
      const dur = Math.round(performance.now() - startedAt);
      // eslint-disable-next-line no-console
      console.warn('[http:error]', {
        requestId,
        method: (options.method || 'GET').toString(),
        url,
        durationMs: dur,
        name: (e as { name?: string })?.name,
        message: (e as { message?: string })?.message,
      });
    }
    throw e;
  } finally {
    if (debug) {
      const dur = Math.round(performance.now() - startedAt);
      // eslint-disable-next-line no-console
      console.debug('[http:done]', {
        requestId,
        method: (options.method || 'GET').toString(),
        url,
        durationMs: dur,
      });
    }
  }

  if (debug) {
    const dur = Math.round(performance.now() - startedAt);
    // eslint-disable-next-line no-console
    console.debug('[http:response]', {
      requestId,
      method: (options.method || 'GET').toString(),
      url,
      status: response.status,
      ok: response.ok,
      durationMs: dur,
    });
  }

  // Handle 401 Unauthorized - token expired
  if (response.status === 401) {
    // Try silent refresh once, then retry the original request.
    if (!_retry) {
      if (!refreshInFlight) {
        refreshInFlight = tryRefreshAccessToken().finally(() => {
          refreshInFlight = null;
        });
      }
      const ok = await refreshInFlight;
      if (ok) {
        if (debug) {
          // eslint-disable-next-line no-console
          console.info('[http:retry]', { requestId, url, reason: '401_refresh_succeeded' });
        }
        return fetchWithAuth(url, options, true);
      }
    }
    if (debug) {
      // eslint-disable-next-line no-console
      console.warn('[http:auth_expired]', { requestId, url });
    }
    notifyAuthExpired();
  }

  return response;
};

/**
 * Trigger a browser download for an authenticated `/api/...` URL.
 *
 * Browsers cannot attach an `Authorization` header to plain navigation
 * (`<a href>`, `location.href`), so any `/api/*` endpoint that
 * requires Cognito auth will reject those requests with 401. For absolute URLs
 * (e.g. S3 presigned URLs) the URL itself carries the credentials and we can
 * trigger normal browser download navigation. For relative URLs we fetch the body via
 * `fetchWithAuth` and stream it into a blob URL that the browser can save.
 *
 * @param url      Either an absolute http(s) URL or a relative path beginning
 *                 with `/api/...`.
 * @param filename Suggested filename for the saved file. Defaults to the last
 *                 path segment of `url`.
 */
export const downloadAuthenticatedFile = async (url: string, filename?: string): Promise<void> => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    const link = document.createElement('a');
    link.href = url;
    if (filename) link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }

  const res = await fetchWithAuth(url);
  if (!res.ok) {
    throw new Error(`Download failed: ${res.status}`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);

  const inferredName = (() => {
    if (filename) return filename;
    const path = url.split('?')[0];
    const last = path.split('/').filter(Boolean).pop() || '';
    return decodeURIComponent(last) || 'download';
  })();

  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = inferredName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoke after the navigation actually starts.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

export const AUTH_EVENTS = {
  expired: AUTH_EXPIRED_EVENT,
} as const;

export const AUTH_STORAGE_KEYS = {
  expiredFlag: AUTH_EXPIRED_FLAG_KEY,
} as const;

export const AUTH_LOCAL_KEYS = {
  token: AUTH_TOKEN_KEY,
  refreshToken: AUTH_REFRESH_TOKEN_KEY,
  username: AUTH_USERNAME_KEY,
} as const;
